/**
 * CountryLanding — the "cohort claims" landing page for a country
 * (e.g. `/countries/uk`). Renders a handful of striking sourced
 * destination stats above the fold so a family lands and instantly
 * sees WhichUniversityAdvisor operates on verified numbers, not
 * generic ranking chatter.
 *
 * Data comes from `GET /api/countries/{code}/cohort-claims`, which
 * aggregates `outcomes.destinations.top_employers` across every
 * editorial degree whose university sits in that country.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Loader2,
  Globe,
} from "lucide-react";

const SUPPORTED = {
  uk: {
    label: "United Kingdom",
    tagline:
      "Real HESA Graduate Outcomes numbers — who actually gets hired where.",
    hero:
      "Every UK undergraduate degree we cover carries the destinations survey attached, so you can see where a cohort actually ends up before you send in a UCAS application.",
    explore_href: "/uk-visa-friendly-degrees",
    explore_label: "Browse visa-friendly UK degrees",
  },
  ie: {
    label: "Ireland",
    tagline:
      "HEA Graduate Outcomes + Trinity's First Destinations — the Big-Tech pipeline in numbers.",
    hero:
      "Dublin is EMEA HQ for Google, Meta, LinkedIn, Amazon, Stripe. We track which Irish cohorts feed each and at what share.",
    explore_href: "/advanced?country=IE",
    explore_label: "Advanced search · Ireland",
  },
  nl: {
    label: "Netherlands",
    tagline:
      "TU Delft, TU/e, Amsterdam — the destinations behind the English-taught bachelor boom.",
    hero:
      "Dutch bachelors are almost entirely English-taught with EU tuition well below UK sticker. We surface the actual first-destination stats so the value case is honest.",
    explore_href: "/advanced?country=NL",
    explore_label: "Advanced search · Netherlands",
  },
  de: {
    label: "Germany",
    tagline:
      "TUM, LMU, RWTH — the engineering pipeline into Munich Big Tech and DAX30 corporates.",
    hero:
      "Germany's public unis charge near-zero tuition. We attach real career-service destinations so you can see whether the trade-off in language and cohort size pays off.",
    explore_href: "/advanced?country=DE",
    explore_label: "Advanced search · Germany",
  },
  fr: {
    label: "France",
    tagline:
      "Grande Écoles + French universities — English-taught tracks with real destinations.",
    hero:
      "France's Grande Écoles route English-taught undergrads straight into Paris, London and NY roles. We attach the sourced % of cohort for each.",
    explore_href: "/advanced?country=FR",
    explore_label: "Advanced search · France",
  },
  au: {
    label: "Australia",
    tagline:
      "QILT Graduate Outcomes across the Group of Eight, QUT and UTS — employment and salary in one place.",
    hero:
      "Every Australian undergraduate degree we cover carries the QILT (Quality Indicators for Learning and Teaching) survey attached, so you can see what share of graduates land jobs within 4 months and what they earn — direct from the Australian Government.",
    explore_href: "/advanced?country=AU",
    explore_label: "Advanced search · Australia",
  },
  us: {
    label: "United States",
    tagline:
      "US Dept of Education Scorecard — real federal tax-return earnings, five years post-graduation.",
    hero:
      "Every US degree we index carries the College Scorecard 5-year earnings tag. These aren't survey-inflated self-reports — they're the median earnings from actual federal tax filings, five years after graduation, broken down by university and subject.",
    explore_href: "/advanced?country=US",
    explore_label: "Advanced search · United States",
  },
};

export default function CountryLanding() {
  const { code } = useParams();
  const country = SUPPORTED[(code || "").toLowerCase()];
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    if (!country) {
      setState({ loading: false, data: null, err: "unsupported" });
      return;
    }
    setState({ loading: true, data: null, err: null });
    api
      .get(`/countries/${code.toLowerCase()}/cohort-claims?limit=15`)
      .then((r) => setState({ loading: false, data: r.data, err: null }))
      .catch((e) => setState({ loading: false, data: null, err: e }));
  }, [code, country]);

  if (!country) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Globe className="h-10 w-10 mx-auto text-stone-400" />
        <h1 className="mt-4 font-editorial text-3xl">Country not covered yet</h1>
        <p className="mt-2 text-stone-600">
          We're rolling out country landing pages gradually. Try{" "}
          <Link to="/countries/uk" className="text-[#4C1D95] underline">
            /countries/uk
          </Link>
          ,{" "}
          <Link to="/countries/nl" className="text-[#4C1D95] underline">
            /nl
          </Link>{" "}
          or{" "}
          <Link to="/countries/ie" className="text-[#4C1D95] underline">
            /ie
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid={`country-landing-${code}`}>
      <Link
        to="/explore"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="country-landing-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All countries
      </Link>

      <div className="mt-4">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Cohort claims
        </div>
        <h1
          data-testid="country-landing-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          Studying in {country.label}?
        </h1>
        <p className="mt-3 text-stone-700 max-w-2xl leading-relaxed text-lg">
          {country.tagline}
        </p>
        <p className="mt-3 text-stone-600 max-w-2xl leading-relaxed">
          {country.hero}
        </p>
      </div>

      {state.loading && (
        <div className="mt-10 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading destinations…
        </div>
      )}

      {!state.loading && state.data && (
        <>
          <TrustBar data={state.data} />
          <ClaimsList claims={state.data.claims || []} />
          <ExploreCTA country={country} />
        </>
      )}

      {!state.loading && (!state.data?.claims?.length) && !state.loading && (
        <div
          data-testid="country-landing-empty"
          className="mt-10 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center text-stone-600"
        >
          We don't have sourced destinations for {country.label} yet — the
          editorial team is working through it. Check back after the next
          HESA / HEA cycle publishes.
        </div>
      )}
    </div>
  );
}

function TrustBar({ data }) {
  return (
    <div
      data-testid="country-landing-trust-bar"
      className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex flex-wrap gap-6 items-center"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-700" />
        <div className="text-[13px] text-emerald-900 font-medium">
          Every number below is sourced.
        </div>
      </div>
      <div className="text-[12px] text-stone-600">
        <span className="font-mono font-semibold text-[#002147]">
          {data.total_courses_with_destinations}
        </span>{" "}
        courses with attached destinations survey ·{" "}
        <span className="font-mono font-semibold text-[#002147]">
          {data.total_claims}
        </span>{" "}
        sourced cohort entries
      </div>
    </div>
  );
}

function ClaimsList({ claims }) {
  if (!claims.length) return null;
  return (
    <div className="mt-8">
      <div className="label-uppercase text-[#4C1D95] font-semibold">
        Most striking cohort stats
      </div>
      <ul className="mt-4 space-y-3">
        {claims.map((c, i) =>
          c.claim_type === "aggregate" ? (
            <AggregateClaim key={`agg-${i}`} c={c} i={i} />
          ) : c.claim_type === "us_scorecard" ? (
            <ScorecardClaim key={`us-${i}`} c={c} i={i} />
          ) : (
            <EmployerClaim key={`emp-${i}`} c={c} i={i} />
          )
        )}
      </ul>
    </div>
  );
}

function EmployerClaim({ c, i }) {
  return (
    <li
      data-testid={`country-landing-claim-${i}`}
      className="rounded-md border border-stone-200 bg-white p-4 flex items-start gap-4"
    >
      <div className="shrink-0 min-w-[70px] text-right">
        <div className="font-chip font-bold text-2xl text-[#4C1D95] tabular-nums">
          {c.pct_of_cohort != null ? `${c.pct_of_cohort.toFixed(1)}%` : "—"}
        </div>
        <div className="text-[10px] font-mono text-stone-400 uppercase tracking-wider">
          of cohort
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] text-stone-800 leading-snug">
          of the{" "}
          <Link
            to={`/uni/${c.university_id}`}
            className="font-semibold text-[#002147] hover:underline"
          >
            {c.university_name}
          </Link>{" "}
          <span className="text-stone-600">{c.subject_area}</span> cohort
          go to{" "}
          <span className="font-semibold text-[#002147]">{c.employer}</span>
          {c.role_hint && <span className="text-stone-500"> · {c.role_hint}</span>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-mono text-stone-500">
          {c.survey_year && (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white text-emerald-800 px-1.5 py-0.5">
              <ShieldCheck className="h-2.5 w-2.5" />
              Verified {c.survey_year}
            </span>
          )}
          {c.sample_size && <span>n={c.sample_size}</span>}
          {c.source && (
            <span
              data-testid={`country-landing-claim-${i}-source`}
              className="text-stone-400"
            >
              · {c.source}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function ScorecardClaim({ c, i }) {
  // US Dept of Education Scorecard row: median earnings 5 years
  // post-graduation from federal tax data. No employment_rate column —
  // Scorecard doesn't report one — so this shape is salary-only and
  // rendered with a clear "5 years post-grad" label so parents don't
  // confuse it with a starting salary.
  const pretty = c.median_earnings_5yr_usd
    ? `$${(c.median_earnings_5yr_usd / 1000).toFixed(0)}k`
    : "—";
  return (
    <li
      data-testid={`country-landing-claim-${i}`}
      data-claim-type="us_scorecard"
      className="rounded-md border border-stone-200 bg-white p-4 flex items-start gap-4"
    >
      <div className="shrink-0 min-w-[70px] text-right">
        <div className="font-chip font-bold text-2xl text-[#4C1D95] tabular-nums">
          {pretty}
        </div>
        <div className="text-[10px] font-mono text-stone-400 uppercase tracking-wider">
          median 5yr
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] text-stone-800 leading-snug">
          median federal-tax earnings of{" "}
          <Link
            to={`/uni/${c.university_id}`}
            className="font-semibold text-[#002147] hover:underline"
          >
            {c.university_name}
          </Link>{" "}
          <span className="text-stone-600">{c.subject_area}</span> graduates,
          five years after leaving.
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-mono text-stone-500">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white text-emerald-800 px-1.5 py-0.5">
            <ShieldCheck className="h-2.5 w-2.5" />
            US Scorecard
          </span>
          {c.sample_size && <span>n={c.sample_size}</span>}
          {c.source_url ? (
            <a
              href={c.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`country-landing-claim-${i}-source`}
              className="text-stone-400 hover:text-stone-700"
            >
              · Scorecard ↗
            </a>
          ) : (
            <span
              data-testid={`country-landing-claim-${i}-source`}
              className="text-stone-400"
            >
              · {c.source}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function AggregateClaim({ c, i }) {
  // AU QILT-style row: employment_rate + median salary. Rendered
  // as "78% employed · AUD 92k median" with the university and
  // subject as the header. Keeps the same visual weight as the
  // employer-style rows above.
  const localPretty =
    c.median_starting_salary_local != null && c.median_starting_salary_currency
      ? `${c.median_starting_salary_currency} ${(c.median_starting_salary_local / 1000).toFixed(0)}k`
      : "—";
  return (
    <li
      data-testid={`country-landing-claim-${i}`}
      data-claim-type="aggregate"
      className="rounded-md border border-stone-200 bg-white p-4 flex items-start gap-4"
    >
      <div className="shrink-0 min-w-[70px] text-right">
        <div className="font-chip font-bold text-2xl text-[#4C1D95] tabular-nums">
          {c.employment_rate_pct != null ? `${c.employment_rate_pct}%` : "—"}
        </div>
        <div className="text-[10px] font-mono text-stone-400 uppercase tracking-wider">
          employed
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] text-stone-800 leading-snug">
          of{" "}
          <Link
            to={`/uni/${c.university_id}`}
            className="font-semibold text-[#002147] hover:underline"
          >
            {c.university_name}
          </Link>{" "}
          <span className="text-stone-600">{c.subject_area}</span>{" "}
          graduates find work within 4 months
          {c.median_starting_salary_local != null && (
            <>
              , earning{" "}
              <span className="font-semibold text-[#002147]">
                {localPretty}
              </span>{" "}
              median
            </>
          )}
          .
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-mono text-stone-500">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white text-emerald-800 px-1.5 py-0.5">
            <ShieldCheck className="h-2.5 w-2.5" />
            QILT 2024
          </span>
          {c.student_satisfaction_pct != null && (
            <span>{c.student_satisfaction_pct}% student satisfaction</span>
          )}
          {c.sample_size && <span>n={c.sample_size}</span>}
          {c.source && (
            <span
              data-testid={`country-landing-claim-${i}-source`}
              className="text-stone-400"
            >
              · {c.source}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function ExploreCTA({ country }) {
  return (
    <div className="mt-10 rounded-lg border border-[#4C1D95]/20 bg-[#4C1D95]/[0.03] p-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="font-editorial text-xl text-[#002147]">
          Ready to filter to your fit?
        </div>
        <div className="text-[13px] text-stone-600 mt-1">
          Run advanced search with grades, budget and subject to see how the
          cohort claims above map to what you can actually get into.
        </div>
      </div>
      <Link
        to={country.explore_href}
        data-testid="country-landing-cta"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
      >
        {country.explore_label} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
